import React, { useState, ReactElement, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReducerState } from "Redux/store";
import * as Styled from "./styles";
import Filters from "Components/Filters";
import Loading from "Components/Loading";
import { useLocation } from "react-router-dom";
import { TSegmentsName } from "Services/Segment/segment";
import ListItem from "./ListItem";
import SeeMore from "Components/Buttons/SeeMore";
import { UseQueryResult } from "@tanstack/react-query";
import { IPagination } from "Services/pagination";
import { IAdTracking } from "Services/Tracking/tracking";
import { createTrack } from "Helpers/tracking";
import localeActions from "Redux/Locale/actions";
import { TSortingValue } from "Components/Sorting";
import Pagination2 from "Components/Pagination2";
import Filters2 from "Components/Filters2";
import CarouselStore from "Views/Stores/List/CarouselStore";
import Banner from "../../Assets/Images/partner_banner.jpg";
import Banner2 from "../../Assets/Images/home_banner.jpg";
import { TBannerResponse } from "Services/Banners/banner";
import api from "Helpers/Api";

interface List2Props<TItem> {
  children?: ReactElement;
  segment?: TSegmentsName;
  useData(params: any): UseQueryResult<IPagination<TItem>, unknown>;
  dataParams: any;
  ItemLayout: React.FC<TItem>;
  hideSearch?: boolean;
  hidePagination?: boolean;
  hideFilters?: boolean;
  seeMoreLink?: string;
  seeMoreText?: string;
  scrollToTopOnPageChange?: boolean;
  filters?: { [key: string]: any };
  sorting?: TSortingValue;
  inline?: boolean;
  row?: number;
  hover?: boolean;
  size?: number;
  grid?: number;
  displayOn?: boolean;
  setDisplayOn?: React.Dispatch<React.SetStateAction<boolean>>;
  searchData?: string | undefined;
}

interface BannerImage extends TBannerResponse {
  width: number;
  height: number;
}

export default function List2<TItem>({
  children,
  segment,
  useData,
  dataParams,
  ItemLayout,
  hideSearch = false,
  hidePagination = false,
  hideFilters = false,
  seeMoreLink,
  seeMoreText = "Ver mais",
  scrollToTopOnPageChange = true,
  filters,
  sorting,
  inline = false,
  row = 3,
  hover = true,
  size,
  grid,
  displayOn,
  setDisplayOn,
  searchData,
}: List2Props<TItem & { id?: string }>) {
  const location = useLocation();
  const dispatch = useDispatch();
  const params = new URLSearchParams(location.search);
  const searchParams = params.get("s") || "";
  const { mobile } = useSelector((state: ReducerState) => state.locale);
  const { gaId, tracking } = useSelector((state: ReducerState) => state.locale);
  const { user } = useSelector((state: ReducerState) => state.auth);
  const [search, setSearch] = useState(searchData || searchParams);
  const [page, setPage] = useState(1);
  const isMobileMenuOpen = displayOn && mobile;

  const { data: response, isLoading, isError } = useData({
    ...dataParams,
    search,
    ...sorting,
    page,
  });

  const data = response && response.data;
  const isFullWidthRoute = /^\/(produtos|produto|stores|secoes-produtos)(\/|$)/.test(
    location.pathname
  );
  let sizeList = 300;
  if (/^\/(produtos|produto|secoes-produtos)(\/|$)/.test(location.pathname)) {
    window.innerWidth < 640
      ? (sizeList = 130)
      : window.innerWidth < 768
      ? (sizeList = 170)
      : window.innerWidth < 800
      ? (sizeList = 180)
      : (sizeList = 300);
  } else if (/^\/stores\/.+/.test(location.pathname)) {
    // stores/ seguido de algo
    sizeList = 160;
  } else if (/^\/stores\/?$/.test(location.pathname)) {
    // apenas stores/ ou stores (sem nada após)
    sizeList = 250;
  }

  const bannerList =
    data &&
    data.length > grid! * 2 &&
    /^\/(produtos)(\/|$)/.test(location.pathname);
  /**TODO
   * This function depends on the states "tracking" and "filters"
   * but because this states are frequently updated
   * this function causes multiple re-renders
   * the current solution is to remove them
   * from the dependency list
   */
  const addListToTracking = useCallback(() => {
    if (segment === "products") {
      const newTracking: IAdTracking[] = data
        ? data
            .filter(
              item =>
                tracking &&
                !tracking.find(trackingItem => trackingItem.ITEM_ID === item.id)
            )
            .map((item, index) => {
              const event: IAdTracking = {
                TYPE: "ITEM_DISPLAY",
                ITEM_TYPE: "PROD",
                GA_ID: gaId,
                USER_ID: user ? user.id : "",
                ITEM_ID: item.id,
                DATA: {
                  POS: `${(index % row) + 1}, ${Math.floor(index / row) + 1}`,
                },
                PAGE_REF: location.pathname,
                TAGS: "mode=model1,version=v2,test=a",
              };
              return createTrack(event, segment ? segment : "products");
            })
        : [];

      if (filters) {
        const filtersString = Object.keys(filters)
          .filter(filter => filters[filter])
          .map(filter => `${filter}=${filters[filter]}`)
          .join("&");

        const filter: IAdTracking = {
          TYPE: "PAGE_SEARCH",
          GA_ID: gaId,
          USER_ID: user ? user.id : "",
          DATA: {
            SEARCH: search,
            FILTER: filtersString,
          },
          PAGE_REF: location.pathname,
          TAGS: "mode=model1,version=v2,test=a",
        };

        tracking &&
          !tracking.find(
            trackingItem =>
              JSON.stringify(trackingItem) ===
              JSON.stringify(
                createTrack(filter, segment ? segment : "products")
              )
          ) &&
          newTracking.push(createTrack(filter, segment ? segment : "products"));
      }

      dispatch(localeActions.updateTracking([...tracking, ...newTracking]));
    }
  }, [segment, data, dispatch, gaId, user, row, location.pathname, search]);

  useEffect(() => addListToTracking(), [addListToTracking]);

  const addProductToTracking = (index: number, id: string) => {
    if (segment) {
      const event: IAdTracking = {
        TYPE: "ITEM_VIEW",
        GA_ID: gaId,
        USER_ID: user ? user.id : "",
        ITEM_ID: id,
        DATA: {
          POS: `${(index % row) + 1}, ${Math.floor(index / row) + 1}`,
        },
        PAGE_REF: location.pathname,
        TAGS: "mode=model1,version=v2,test=a",
      };

      dispatch(
        localeActions.addTracking(
          createTrack(event, segment ? segment : "products")
        )
      );
    }
  };

  const clickOnProduct = (index: number, id: string) => {
    if (segment) {
      const event: IAdTracking = {
        TYPE: "ITEM_CLICK",
        GA_ID: gaId,
        USER_ID: user ? user.id : "",
        ITEM_ID: id,
        PAGE_REF: location.pathname,
        TAGS: "mode=model1,version=v2,test=a",
      };

      dispatch(
        localeActions.addTracking(
          createTrack(event, segment ? segment : "products")
        )
      );
    }
  };

  const placeholderLength =
    !data || data.length % row === 0 ? 0 : ((data.length % row) - row) * -1;

  const createPlaceholders = (number: number) => {
    const placeholders = [];

    for (let i = 1; i <= number; i++) {
      placeholders.push(
        <Styled.EmptyItem
          size={100 / row}
          inline={inline}
          hover={hover}
          isOne={false}
          key={`placeholder-${i}`}
        />
      );
    }

    return placeholders;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPage(1), [...Object.values(dataParams), search, sorting]);
  const [banners, setBanners] = useState<BannerImage[]>([]);
  const getImageDimensions = (
    url: string
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = reject;
    });
  };

  const getBanner = async () => {
    try {
      const response = await api.get<TBannerResponse[]>("/banners/active");
      const dataFilter = response.data.filter(
        e => e.position == "meio_produtos"
      );
      const bannersWithDimensions = await Promise.all(
        dataFilter.map(async banner => {
          try {
            const { width, height } = await getImageDimensions(banner.image);
            return { ...banner, width, height };
          } catch {
            return { ...banner, width: 0, height: 0 }; // fallback se a imagem falhar
          }
        })
      );

      setBanners(bannersWithDimensions);
    } catch (error) {
      setBanners([]);
    }
  };

  useEffect(() => {
    getBanner();
  }, []);

  useEffect(() => {
    searchData !== undefined && setSearch(searchData);
  }, [searchData]);

  return (
    <>
      <Styled.Content id="list2-content" fullWidth={isFullWidthRoute}>
        <Filters2
          total={data?.length}
          setSearch={setSearch}
          searchHidden={hideSearch}
          displayOn={displayOn}
          setDisplayOn={setDisplayOn}
        >
          {children}
        </Filters2>
        {isLoading ? (
          <Loading>{createPlaceholders(12).map(ph => ph)}</Loading>
        ) : (
          <Styled.ListContainer id="list2-container">
            {isError ? (
              <Styled.NoResults>
                <div>Erro no servidor, tente mais tarde</div>
              </Styled.NoResults>
            ) : data && data.length !== 0 ? (
              <>
                <Styled.List
                  inline={inline}
                  size={isFullWidthRoute ? sizeList : size}
                  grid={grid}
                  className="products-list"
                >
                  {data &&
                    data.length &&
                    data.map((item: any, key: any) => (
                      <React.Fragment key={`list-item-${key}`}>
                        {key == grid! * 2 &&
                          bannerList &&
                          banners &&
                          banners.length > 0 && (
                            <div style={{ gridColumn: "1 / -1" }}>
                              <CarouselStore
                                isList={true}
                                slides={banners}
                                isSponsored={false}
                              />
                            </div>
                          )}
                        <ListItem
                          isOne={data.length == 1}
                          hover={hover}
                          size={100 / row}
                          inline={inline}
                          onEnterViewport={() =>
                            addProductToTracking(key, item.id)
                          }
                          click={() => clickOnProduct(key, item.id)}
                        >
                          <ItemLayout {...item} />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  {!mobile &&
                    placeholderLength > 0 &&
                    createPlaceholders(placeholderLength).map(ph => ph)}
                </Styled.List>
                {!hidePagination && (
                  <Pagination2
                    page={page}
                    perPage={response.perPage}
                    lastPage={response.lastPage}
                    onPageChange={(page: number) => {
                      setPage(page);
                      scrollToTopOnPageChange && window.scrollTo(0, 0);
                    }}
                  />
                )}
                {seeMoreLink && seeMoreText && (
                  <SeeMore redirectLink={seeMoreLink}>{seeMoreText}</SeeMore>
                )}
              </>
            ) : (
              <Styled.NoResults>
                {isError ? (
                  <div>Erro no servidor, tente mais tarde</div>
                ) : (
                  <div>Nenhum resultado foi encontrado</div>
                )}
              </Styled.NoResults>
            )}
          </Styled.ListContainer>
        )}
      </Styled.Content>
    </>
  );
}
